import { Amplify } from 'aws-amplify';
import {
  associateWebAuthnCredential,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut as amplifySignOut,
  updateMFAPreference,
  verifyTOTPSetup,
  type SignInOutput,
} from 'aws-amplify/auth';
import type { AppConfig } from '../config';

// Cognito behind our own screens (docs/architecture.md §9a). Amplify Auth is used headless:
// it speaks Cognito's API (SRP, challenges, TOTP, passkeys); every pixel is ours.

export function configureCognito(config: AppConfig): void {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.cognito.userPoolId,
        userPoolClientId: config.cognito.webClientId,
        loginWith: { email: true },
      },
    },
  });
}

/** What the sign-in screen should show next. */
export type NextStep = 'done' | 'newPassword' | 'totp' | 'resetPassword' | 'unsupported';

function next(output: SignInOutput): NextStep {
  switch (output.nextStep.signInStep) {
    case 'DONE':
      return 'done';
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return 'newPassword';
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return 'totp';
    // An admin unlocked the account (forced reset) or the password was reset: a code was emailed.
    case 'RESET_PASSWORD':
      return 'resetPassword';
    default:
      return 'unsupported';
  }
}

export async function signInWithPassword(email: string, password: string): Promise<NextStep> {
  // SRP: the password itself never leaves the browser.
  return next(
    await signIn({ username: email, password, options: { authFlowType: 'USER_SRP_AUTH' } }),
  );
}

export async function signInWithPasskey(email: string): Promise<NextStep> {
  return next(
    await signIn({
      username: email,
      options: { authFlowType: 'USER_AUTH', preferredChallenge: 'WEB_AUTHN' },
    }),
  );
}

export async function submitNewPassword(password: string): Promise<NextStep> {
  return next(await confirmSignIn({ challengeResponse: password }));
}

export async function submitTotp(code: string): Promise<NextStep> {
  return next(await confirmSignIn({ challengeResponse: code }));
}

export async function isSignedIn(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

export async function accessToken(): Promise<string | undefined> {
  const session = await fetchAuthSession();
  return session.tokens?.accessToken.toString();
}

export async function signOut(): Promise<void> {
  await amplifySignOut();
}

/** Starts authenticator setup; returns the secret and an otpauth:// URI for the QR code. */
export async function beginTotpSetup(account: string): Promise<{ secret: string; uri: string }> {
  const details = await setUpTOTP();
  return {
    secret: details.sharedSecret,
    uri: details.getSetupUri('ClairoHealth', account).toString(),
  };
}

export async function finishTotpSetup(code: string): Promise<void> {
  await verifyTOTPSetup({ code });
  await updateMFAPreference({ totp: 'PREFERRED' });
}

export async function addPasskey(): Promise<void> {
  await associateWebAuthnCredential();
}

/** Emails a reset code (forgot password, or a fresh code after an admin unlock). */
export async function sendResetCode(email: string): Promise<void> {
  await resetPassword({ username: email });
}

export async function completeReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
}

/** Maps Cognito/Amplify errors to translation keys under auth.errors. */
export function authErrorKey(err: unknown): string {
  const name = (err as { name?: string } | null)?.name ?? '';
  const message = (err as { message?: string } | null)?.message ?? '';
  // Our lockout trigger (ACCOUNT_LOCKED) or an account disabled after it.
  if (/ACCOUNT_LOCKED|User is disabled/i.test(message)) return 'auth.errors.locked';
  if (name === 'PasswordResetRequiredException') return 'auth.errors.resetRequired';
  if (/attempts exceeded|LimitExceeded|TooManyRequests/i.test(name + message))
    return 'auth.errors.tooManyAttempts';
  if (/Invalid session|session is expired/i.test(message)) return 'auth.errors.sessionExpired';
  if (name === 'NotAuthorizedException' || name === 'UserNotFoundException')
    return 'auth.errors.invalidCredentials';
  if (name === 'CodeMismatchException' || name === 'EnableSoftwareTokenMFAException')
    return 'auth.errors.codeMismatch';
  if (name === 'ExpiredCodeException') return 'auth.errors.sessionExpired';
  if (name === 'InvalidPasswordException') return 'auth.errors.passwordPolicy';
  if (/passkey|webauthn|NotAllowedError|PasskeyError/i.test(name + message))
    return 'auth.errors.passkeyUnavailable';
  return 'auth.errors.generic';
}
